import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Users, TrendingUp, DollarSign, Activity, Mail, Search, Plus, X, ExternalLink, Eye, ShieldCheck, Tag, Receipt, Download, FileText, AlertCircle, Pause, Play, Archive, ArchiveRestore, History, Send, Trash2, UserPlus, UserCheck, UserMinus, ArrowUpCircle, ArrowDownCircle, RefreshCcw, CheckCircle2, Loader2, FileSearch, Link2, Pencil, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils";
import { formatDateAU, formatDateTimeAU } from "@/lib/formatDateAU";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PartnerBrandingSection from "@/pages/admin/PartnerBrandingSection";
import AdminPartnerTerritoryModal from "@/pages/admin/AdminPartnerTerritoryModal";
import PartnerCapabilitiesSection from "@/pages/admin/PartnerCapabilitiesSection";
import AcquisitionHistorySection from "@/pages/admin/AcquisitionHistorySection";
import PartnerReferralSection from "@/pages/admin/PartnerReferralSection";
import { TradePortalInvitePanel } from "@/components/admin/TradePortalInvitePanel";

// Jun 2026 Partner Platform Phase 1 — compact capability badges for the
// partner list. Reads partners.capabilities written by the backfill/admin
// toggles; shows a dash when nothing is enabled.
const CAP_BADGES = [
  { key: "can_refer", label: "Refer", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { key: "can_purchase_kits", label: "Kits", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "can_retail", label: "Retail", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  { key: "invoice_account_enabled", label: "Invoice", cls: "bg-amber-50 text-amber-700 border-amber-200" },
];

function CapabilityBadges({ caps }) {
  const active = CAP_BADGES.filter((b) => caps?.[b.key]);
  if (active.length === 0) return <span className="text-xs text-slate-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {active.map((b) => (
        <span key={b.key} className={`px-1.5 py-0.5 rounded-md border text-[10px] font-bold ${b.cls}`}>{b.label}</span>
      ))}
    </span>
  );
}

const STATUS_PILL = {
  lead:       { bg: "bg-slate-100",   text: "text-slate-700",   label: "Lead" },
  invited:    { bg: "bg-cyan-100",    text: "text-cyan-800",    label: "Invited" },
  engaged:    { bg: "bg-blue-100",    text: "text-blue-800",    label: "Engaged" },
  registered: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Registered" },
  converted:  { bg: "bg-violet-100",  text: "text-violet-800",  label: "Converted" },
  inactive:   { bg: "bg-slate-100",   text: "text-slate-500",   label: "Inactive" },
  // Partner Lifecycle Controls v1 (Feb 2026) — admin-owned states.
  active:     { bg: "bg-emerald-100", text: "text-emerald-800", label: "Active" },
  paused:     { bg: "bg-amber-100",   text: "text-amber-800",   label: "Paused" },
  archived:   { bg: "bg-slate-200",   text: "text-slate-600",   label: "Archived" },
};

// Feb 2026 (production fix): pws_franchisee partners always carry
// `status="lead"` in Mongo as an internal referral-funnel tag (see
// routers/pws_partners.py:317 for the intentional invariant). Rendering
// that literal string on the drawer header made every PWS partner —
// draft or active — look unresolved. For pws_franchisee rows we now
// render the ACTUAL PWS lifecycle state instead.
const PWS_STATUS_PILL = {
  draft:      { bg: "bg-slate-100",   text: "text-slate-700",   label: "Draft" },
  active:     { bg: "bg-emerald-100", text: "text-emerald-800", label: "Active" },
  paused:     { bg: "bg-amber-100",   text: "text-amber-800",   label: "Paused" },
  suspended:  { bg: "bg-rose-100",    text: "text-rose-800",    label: "Suspended" },
  archived:   { bg: "bg-slate-200",   text: "text-slate-600",   label: "Archived" },
};

function pickPartnerStatusPill(partner) {
  if (partner?.partner_type === "pws_franchisee") {
    const pws = (partner.pws_partner_status || "draft").toLowerCase();
    return PWS_STATUS_PILL[pws] || PWS_STATUS_PILL.draft;
  }
  return STATUS_PILL[partner?.status] || STATUS_PILL.lead;
}

const TYPES = [
  { value: "conveyancer", label: "Conveyancer" },
  { value: "real_estate_agent", label: "Real Estate Agent" },
  { value: "buyer_agent", label: "Buyer Agent" },
  { value: "property_manager", label: "Property Manager" },
  { value: "pump_shop", label: "Pump Shop" },
  { value: "plumber", label: "Plumber" },
  { value: "building_inspector", label: "Building Inspector" },
  { value: "water_treatment_provider", label: "Water Treatment Provider" },
  { value: "pws_franchisee", label: "PWS Franchisee" },
  { value: "other", label: "Other" },
];

function Metric({ icon: Icon, label, value, hint, accent = "cyan" }) {
  const styles = {
    cyan: "text-cyan-700 bg-cyan-50",
    emerald: "text-emerald-700 bg-emerald-50",
    violet: "text-violet-700 bg-violet-50",
    amber: "text-amber-700 bg-amber-50",
  }[accent];
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-5">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${styles}`}><Icon className="w-4 h-4" /></div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{label}</div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

/**
 * Feb 2026 — Partners row action: preview + share white-labelled Summary.
 *
 * Two buttons, rendered inline in each pws_franchisee row:
 *   1. "Preview" — opens the admin-authenticated PDF preview in a new
 *      tab (backend redirects with the JWT already applied by the
 *      browser's Authorization header, so no extra work needed).
 *      Fetches the PDF via `api` so we inherit the axios auth
 *      interceptor, then previews it via a blob URL — this avoids
 *      the "download prompt" you'd get from opening the raw endpoint
 *      in a plain `window.open` (which strips auth).
 *   2. "Share link" — mints a signed, time-limited public preview
 *      token via POST /api/admin/partners/:id/summary-preview-token,
 *      copies the resulting URL to the clipboard, and shows the
 *      expiry date in a toast so the admin knows how long the link
 *      will keep working.
 *
 * Row-level clicks propagate to `setSelected(p.id)` in the parent, so
 * every button here calls `e.stopPropagation()` first.
 */
function PartnerReportActions({ partner }) {
  const [busy, setBusy] = useState(null); // "preview" | "share" | null

  const openPreview = async (e) => {
    e.stopPropagation();
    setBusy("preview");
    try {
      const resp = await api.get(
        `/admin/reports/pristine-summary/sample?partner_slug=${encodeURIComponent(partner.partner_slug)}`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Free the blob after the tab has consumed it — 60s is generous
      // for the browser's PDF viewer to finish rendering.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(`Preview failed: ${formatApiError(err)}`);
    } finally {
      setBusy(null);
    }
  };

  const copyShareLink = async (e) => {
    e.stopPropagation();
    setBusy("share");
    try {
      const { data } = await api.post(
        `/admin/partners/${partner.id}/summary-preview-token`,
      );
      // Prefer the explicit URL returned by the backend when
      // FRONTEND_URL is configured; fall back to constructing from the
      // browser origin so preview works even before FRONTEND_URL is set.
      let shareUrl = data.share_url || "";
      if (!/^https?:\/\//i.test(shareUrl)) {
        shareUrl = `${window.location.origin}${shareUrl}`;
      }
      try {
        await navigator.clipboard.writeText(shareUrl);
        const expiry = data.expires_at
          ? formatDateAU(data.expires_at)
          : `${data.ttl_days || 14} days`;
        toast.success(
          `Share link copied · expires ${expiry}`,
          { description: shareUrl.length > 60 ? shareUrl.slice(0, 60) + "…" : shareUrl },
        );
      } catch {
        // Older browsers / non-secure contexts — surface the URL in the
        // toast so the admin can copy manually.
        toast.info("Preview link ready (copy manually)", { description: shareUrl });
      }
    } catch (err) {
      toast.error(`Share link failed: ${formatApiError(err)}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={openPreview}
        disabled={busy === "preview"}
        data-testid={`partner-summary-preview-${partner.id}`}
        title="Preview white-labelled Summary Report (sample data)"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"
      >
        {busy === "preview" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSearch className="w-3.5 h-3.5" />}
        Preview
      </button>
      <button
        type="button"
        onClick={copyShareLink}
        disabled={busy === "share"}
        data-testid={`partner-summary-share-${partner.id}`}
        title="Copy a shareable public preview link (expires in 14 days)"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
      >
        {busy === "share" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
        Share
      </button>
    </span>
  );
}

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [pmAppPending, setPmAppPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  // Feb 2026 — archived partners are hidden by default so the list
  // focuses on active/paused/draft rows. Toggle in the filter row
  // brings them back for admin review. When the user explicitly picks
  // "Archived" in the status Select, the toggle auto-flips on so
  // their choice wins over the default hide.
  const [showArchived, setShowArchived] = useState(false);

  // Phase 2L-A139-fu — Deep-link support for the PWS Partner Overview
  // "Open" action. Any admin surface can now route to
  // `/admin/partners?open=<partner_id>` and land with the correct
  // PartnerDetail modal pre-opened. Closing the modal strips the
  // param so a browser refresh doesn't spring it back open.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) setSelected(openId);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (typeFilter) params.partner_type = typeFilter;
    if (searchQ) params.q = searchQ;
    Promise.all([
      api.get("/admin/partners", { params }).then(({ data }) => setPartners(data)),
      api.get("/admin/partners/metrics").then(({ data }) => setMetrics(data)),
      api.get("/admin/engagement/recent?limit=30").then(({ data }) => setRecentEvents(data)),
      api.get("/admin/property-manager-applications/metrics")
         .then(({ data }) => setPmAppPending(data?.pending || 0))
         .catch(() => setPmAppPending(0)),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, typeFilter]);

  return (
    <PortalShell>
      <div data-testid="admin-partners-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">Partner Referral Engine</div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">Partners</h1>
            <p className="text-slate-500 mt-1 text-sm">Conveyancers, agents, plumbers — everyone who refers properties to us.</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/partner/guide"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-partner-guide-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:border-cyan-500 hover:text-cyan-700 transition-colors"
            >
              <BookOpen className="w-4 h-4" /> Partner Guide
            </a>
            <Button data-testid="admin-partner-create-btn" onClick={() => setShowCreate(true)} className="btn-aqua rounded-full font-semibold">
              <Plus className="w-4 h-4 mr-1.5" /> Add partner
            </Button>
          </div>
        </div>

        {/* Phase 2L-A111 — Property Manager applications quick-jump.
            Surfaces the pending count + opens the dedicated queue. */}
        <Link
          to="/admin/partners/applications"
          data-testid="pm-applications-tile"
          className="mt-5 flex items-center gap-4 rounded-2xl border border-cyan-200 bg-cyan-50/50 hover:bg-cyan-50 transition px-5 py-4 group"
        >
          <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
            <span className="text-cyan-700 text-lg font-bold" data-testid="pm-applications-pending-count">{pmAppPending}</span>
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm">
              Property Manager applications · awaiting review
            </div>
            <div className="text-xs text-slate-500">
              Public applicants from /property-managers/apply land here. Approval auto-creates partner + login + welcome email.
            </div>
          </div>
          <span className="text-cyan-700 text-sm font-semibold group-hover:underline">Open queue →</span>
        </Link>

        {/* Aware Partner Network — Phase 1A quick-jump.
            Manual draft franchisee management. No emails sent. */}
        <Link
          to="/admin/partners/network"
          data-testid="pws-network-tile"
          className="mt-3 flex items-center gap-4 rounded-2xl border border-amber-200 bg-amber-50/50 hover:bg-amber-50 transition px-5 py-4 group"
        >
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-amber-700" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm">
              PWS Franchisee Network · Phase 1A
            </div>
            <div className="text-xs text-slate-500">
              Manually create + review PWS franchisee partner rows. Drafts only — no automated emails or logins.
            </div>
          </div>
          <span className="text-amber-700 text-sm font-semibold group-hover:underline">Open network →</span>
        </Link>

        {/* Phase 2L-A134 — Partner Pricing Controls v1 quick-jump.
            Admin-only global partner-type pricing defaults table. Single-
            partner overrides live inside each partner's detail modal. */}
        <Link
          to="/admin/pricing/partner-types"
          data-testid="pricing-controls-tile"
          className="mt-3 flex items-center gap-4 rounded-2xl border border-violet-200 bg-violet-50/50 hover:bg-violet-50 transition px-5 py-4 group"
        >
          <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
            <Tag className="w-5 h-5 text-violet-700" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm">
              Pricing controls · partner-type defaults
            </div>
            <div className="text-xs text-slate-500">
              Set default invoice pricing per partner type (PM, Trade Partners). Single-partner overrides live in each partner's profile.
            </div>
          </div>
          <span className="text-violet-700 text-sm font-semibold group-hover:underline">Open pricing →</span>
        </Link>

        {/* Phase 2L-A139 — PWS Partner Overview (Phase 1, read-only) */}
        <Link
          to="/admin/partners/pws-overview"
          data-testid="pws-partner-overview-tile"
          className="mt-3 flex items-center gap-4 rounded-2xl border border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50 transition px-5 py-4 group"
        >
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm">
              PWS Partner Overview
            </div>
            <div className="text-xs text-slate-500">
              7-phase rollout snapshot per PWS franchisee · profile, pricing, branding, booking, SLA, marketing kit, sample workflow.
            </div>
          </div>
          <span className="text-emerald-700 text-sm font-semibold group-hover:underline">Open overview →</span>
        </Link>

        {/* Jun 2026 — PODAC Stage 1: PWS Partner QR Directory */}
        <Link
          to="/admin/partners/pws-qr-directory"
          data-testid="pws-qr-directory-tile"
          className="mt-3 flex items-center gap-4 rounded-2xl border border-cyan-200 bg-cyan-50/50 hover:bg-cyan-50 transition px-5 py-4 group"
        >
          <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-cyan-700" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm">
              PWS Partner QR Directory
            </div>
            <div className="text-xs text-slate-500">
              Canonical referral links + QR codes per eligible PWS Partner · PODAC lookup exposure and diagnostics.
            </div>
          </div>
          <span className="text-cyan-700 text-sm font-semibold group-hover:underline">Open directory →</span>
        </Link>

        {/* Metrics */}
        {metrics && (
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-partner-metrics">
            <Metric icon={Users} label="Total partners" value={metrics.total_partners} hint={`${metrics.by_status?.registered || 0} registered · ${metrics.by_status?.invited || 0} invited`} accent="cyan" />
            <Metric icon={TrendingUp} label="Conversion rate" value={`${metrics.conversion_rate}%`} hint="Registered + Converted ÷ Total" accent="emerald" />
            <Metric icon={DollarSign} label="Revenue attributed" value={`$${metrics.revenue_attributed.toFixed(2)}`} hint="From recorded referrals" accent="violet" />
            <Metric icon={Activity} label="Most active" value={metrics.top_active[0]?.engagement_count || 0} hint={metrics.top_active[0]?.business_name || metrics.top_active[0]?.email || "No engagement yet"} accent="amber" />
          </div>
        )}

        {/* Filters + table */}
        <div className="mt-7 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="partner-search" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search by name or email…" className="pl-10" />
          </div>
          <Select
            value={statusFilter || "all"}
            onValueChange={(v) => {
              const next = v === "all" ? "" : v;
              setStatusFilter(next);
              // Explicit Archived choice must override the default hide.
              if (next === "archived") setShowArchived(true);
            }}
          >
            <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_PILL).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(() => {
            const archivedCount = partners.filter((x) => x.status === "archived").length;
            if (archivedCount === 0) return null;
            return (
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                data-testid="admin-partners-toggle-archived"
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${
                  showArchived
                    ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                    : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}
                title={showArchived
                  ? "Hide archived partners from the list"
                  : "Show archived partners in the list"}
              >
                {showArchived ? <Archive className="w-3.5 h-3.5" /> : <ArchiveRestore className="w-3.5 h-3.5" />}
                {showArchived ? `Hide archived (${archivedCount})` : `Show archived (${archivedCount})`}
              </button>
            );
          })()}
        </div>

        <div className="mt-3 grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3">Partner</th>
                    <th className="text-left px-5 py-3">Type</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-left px-5 py-3">Capabilities</th>
                    <th className="text-left px-5 py-3">Engagement</th>
                    <th className="text-right px-5 py-3">Report</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Hide archived rows unless the toggle is on OR the
                    // admin explicitly filtered by Archived status.
                    const visiblePartners = partners.filter((p) =>
                      showArchived || p.status !== "archived"
                    );
                    if (loading) {
                      return <tr><td colSpan="6" className="text-center py-10 text-slate-500">Loading…</td></tr>;
                    }
                    if (visiblePartners.length === 0) {
                      const totalArchived = partners.filter((p) => p.status === "archived").length;
                      const onlyArchivedLeft = totalArchived > 0 && !showArchived;
                      return (
                        <tr><td colSpan="6" className="text-center py-10 text-slate-500">
                          <Users className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                          {onlyArchivedLeft ? (
                            <>
                              Only archived partners in the current filter.{" "}
                              <button
                                type="button"
                                onClick={() => setShowArchived(true)}
                                className="text-cyan-700 hover:underline font-semibold"
                                data-testid="admin-partners-empty-show-archived"
                              >
                                Show them ({totalArchived})
                              </button>.
                            </>
                          ) : (
                            <>No partners yet. They'll appear here once a certificate is shared with a conveyancer/agent.</>
                          )}
                        </td></tr>
                      );
                    }
                    return visiblePartners.map((p) => {
                      const pill = pickPartnerStatusPill(p);
                      const counts = p.activity_summary || {};
                      const engaged = (counts.email_opened || 0) + (counts.cert_viewed || 0) + (counts.verify_viewed || 0);
                      const isPwsFranchisee = p.partner_type === "pws_franchisee" && !!p.partner_slug;
                      return (
                        <tr key={p.id} data-testid={`partner-row-${p.id}`} onClick={() => setSelected(p.id)} className="border-t border-slate-100 hover:bg-slate-50/50 cursor-pointer">
                          <td className="px-5 py-3.5">
                            <div className="font-medium text-slate-900">{p.business_name || p.contact_name || "—"}</div>
                            <div className="text-xs text-slate-500">{p.email}</div>
                          </td>
                          <td className="px-5 py-3.5 text-slate-700">
                            {TYPES.find((t) => t.value === p.partner_type)?.label || p.partner_type}
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${pill.bg} ${pill.text}`}>{pill.label}</span>
                          </td>
                          <td className="px-5 py-3.5" data-testid={`partner-caps-${p.id}`}>
                            <CapabilityBadges caps={p.capabilities} />
                          </td>
                          <td className="px-5 py-3.5 text-xs text-slate-600">
                            <span className="inline-flex items-center gap-2">
                              <span><Mail className="inline w-3 h-3 mr-0.5" />{counts.email_sent || 0}</span>
                              <span><Eye className="inline w-3 h-3 mr-0.5" />{counts.email_opened || 0}</span>
                              <span>· {engaged} engagements</span>
                            </span>
                          </td>
                          <td className="px-3 py-3.5 text-right">
                            {isPwsFranchisee ? (
                              <PartnerReportActions partner={p} />
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sidebar — recent engagement */}
          <aside className="bg-white rounded-3xl border border-slate-200 p-5">
            <h2 className="font-display font-semibold text-slate-900 mb-3">Recent activity</h2>
            {recentEvents.length === 0 ? (
              <p className="text-sm text-slate-500">No engagement yet.</p>
            ) : (
              <ol className="space-y-2.5 text-xs">
                {recentEvents.slice(0, 15).map((ev) => (
                  <li key={ev.id} className="flex items-start gap-2 pb-2 border-b border-slate-100 last:border-0">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-900 font-medium">{ev.event_type.replace(/_/g, " ")}</div>
                      <div className="text-slate-500 truncate">{ev.partner_email || "—"}</div>
                      <div className="text-slate-400 text-[10px]">{formatDateTimeAU(ev.at)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </aside>
        </div>

        {/* Top referrers */}
        {metrics?.top_referrers?.length > 0 && (
          <div className="mt-7 bg-white rounded-3xl border border-slate-200 p-6">
            <h2 className="font-display font-semibold text-slate-900 mb-3">Top referrers</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {metrics.top_referrers.slice(0, 6).map((p) => (
                <div key={p.id} className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-400">{TYPES.find((t) => t.value === p.partner_type)?.label}</div>
                  <div className="mt-0.5 font-semibold text-slate-900">{p.business_name || p.email}</div>
                  <div className="mt-1.5 flex items-baseline gap-3 text-sm">
                    <span className="text-slate-900 font-display text-xl font-bold">{p.referral_count}</span>
                    <span className="text-slate-500 text-xs">referrals</span>
                    <span className="ml-auto text-emerald-700 font-semibold">${p.revenue.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {selected && <PartnerDetail partnerId={selected} onClose={() => {
        setSelected(null);
        // Strip the ?open= deep-link param so a refresh doesn't reopen.
        if (searchParams.get("open")) {
          const next = new URLSearchParams(searchParams);
          next.delete("open");
          setSearchParams(next, { replace: true });
        }
        load();
      }} />}
      {showCreate && <PartnerCreate onClose={() => { setShowCreate(false); load(); }} />}
    </PortalShell>
  );
}


function PartnerDetail({ partnerId, onClose }) {
  const [data, setData] = useState(null);
  const [territoryOpen, setTerritoryOpen] = useState(false);
  const [editHeaderOpen, setEditHeaderOpen] = useState(false);
  const reload = () => api.get(`/admin/partners/${partnerId}`).then(({ data }) => setData(data));
  useEffect(() => { reload(); }, [partnerId]);
  if (!data) return null;
  const p = data.partner;
  const pill = pickPartnerStatusPill(p);
  const isPws = p.partner_type === "pws_franchisee";
  const nameMissing = !(p.business_name || "").trim();
  const headerTitle = p.business_name || p.contact_name || p.email;
  return (
    <div data-testid="admin-partner-detail" className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-7 my-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">{TYPES.find((t) => t.value === p.partner_type)?.label}</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-2xl font-bold text-slate-900 break-words">{headerTitle}</h2>
              <button
                onClick={() => setEditHeaderOpen(true)}
                data-testid="admin-partner-edit-header"
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 shrink-0"
                title="Edit business name & contact"
                aria-label="Edit business name"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-sm text-slate-500 mt-0.5">{p.email} {p.phone && `· ${p.phone}`}</div>
            {nameMissing && (
              <button
                onClick={() => setEditHeaderOpen(true)}
                data-testid="admin-partner-set-name-cta"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 hover:bg-amber-100 px-3 py-1.5 rounded-full"
              >
                <AlertCircle className="w-3.5 h-3.5" />
                Set business name
              </button>
            )}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span
                data-testid={isPws ? "admin-partner-pws-status-pill" : "admin-partner-status-pill"}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${pill.bg} ${pill.text}`}
              >
                {pill.label}
              </span>
              {/* Feb 2026 (production fix): the Partners.jsx drawer has
                  no header-fields edit form. For PWS partners send the
                  admin to the PWS Partner Network drawer, which has
                  the full Edit details form (business name, contact,
                  email, phone, region, etc.). */}
              {isPws && (
                <Link
                  to={`/admin/partners/network?open=${p.id}`}
                  data-testid="admin-partner-open-in-pws-network-link"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-700 hover:text-cyan-800 hover:underline"
                >
                  Open in PWS Partner Network to edit
                  <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => setTerritoryOpen(true)}
              data-testid="admin-partner-open-territory"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700"
              title="Manage service postcodes, region, commission, ABN, mail-out"
            >
              Territory & Business
            </button>
            <button onClick={onClose} data-testid="admin-partner-detail-close" aria-label="Close partner detail" className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {Object.entries(data.engagement_counts || {}).map(([k, v]) => (
            <div key={k} className="rounded-xl bg-slate-50 p-3 text-center">
              <div className="font-display text-2xl font-bold text-slate-900">{v}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-0.5">{k.replace(/_/g, " ")}</div>
            </div>
          ))}
        </div>

        {/* Phase 2E — revenue metrics */}
        {data.revenue && (
          <div data-testid="partner-revenue-block" className="mt-5 rounded-2xl bg-emerald-50/60 border border-emerald-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Revenue attributed</div>
            <div className="mt-1 flex items-baseline gap-3 flex-wrap">
              <span className="font-display text-3xl font-bold text-emerald-700">${(data.revenue.revenue_total || 0).toFixed(2)}</span>
              <span className="text-xs text-slate-600">{data.revenue.referral_count} referral{data.revenue.referral_count !== 1 ? "s" : ""}</span>
              <span className="text-xs text-slate-600">·</span>
              <span className="text-xs text-slate-600">Avg ${(data.revenue.average_revenue_per_referral || 0).toFixed(2)}/referral</span>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-2 text-[11px]">
              {Object.entries(data.revenue.by_kind || {}).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-emerald-800 font-semibold">
                  {k.replace(/_/g, " ")}: ${v.revenue.toFixed(2)} ({v.count})
                </span>
              ))}
              {Object.keys(data.revenue.by_kind || {}).length === 0 && <span className="text-slate-500">No revenue events yet for this partner.</span>}
            </div>
            <div className="mt-2.5 text-[11px] text-slate-500">
              {data.revenue.properties_referred} property/properties · {data.revenue.customers_referred} customer(s) · last referral {formatDateAU(data.revenue.last_referral_at)}
            </div>
          </div>
        )}

        {data.referrals.length > 0 && (
          <div className="mt-5">
            <h3 className="font-display font-semibold text-slate-900 mb-2">Referrals ({data.referral_count})</h3>
            <ul className="space-y-1.5 text-sm">
              {data.referrals.slice(0, 8).map((r) => (
                <li key={r.id} className="flex justify-between p-2.5 rounded-xl bg-slate-50">
                  <span className="text-slate-700">{r.property_address || r.property_id?.slice(0, 8) || "—"}</span>
                  <span className="font-mono text-emerald-700 font-semibold">${(r.revenue_generated || 0).toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-5">
          <h3 className="font-display font-semibold text-slate-900 mb-2">Recent activity</h3>
          <ol className="space-y-1.5 text-xs max-h-60 overflow-y-auto">
            {data.recent_activity.slice(0, 30).map((ev) => (
              <li key={ev.id} className="flex justify-between gap-3 p-2 rounded-lg bg-slate-50">
                <span className="font-medium text-slate-700">{ev.event_type.replace(/_/g, " ")}</span>
                <span className="text-slate-400">{formatDateTimeAU(ev.at)}</span>
              </li>
            ))}
          </ol>
        </div>

        <PartnerCapabilitiesSection partner={p} />
        <AcquisitionHistorySection partnerId={p.id} />

        <PartnerReferralSection partner={p} />

        <PartnerPricingSection partner={p} />

        <PartnerInvoiceLedgerSection partner={p} />

        <PartnerBrandingSection partnerId={p.id} partnerEmail={p.email} />

        {(p.partner_type === "property_manager") && (
          <AgencyUsersSection partner={p} />
        )}

        <PartnerLifecycleSection partner={p} onChanged={async () => {
          const { data: fresh } = await api.get(`/admin/partners/${partnerId}`);
          setData(fresh);
        }} onDeleted={onClose} />
      </div>
      {territoryOpen && (
        <AdminPartnerTerritoryModal
          partner={p}
          onClose={() => setTerritoryOpen(false)}
          onSaved={() => { reload(); }}
        />
      )}
      {editHeaderOpen && (
        <PartnerEditHeaderModal
          partner={p}
          onClose={() => setEditHeaderOpen(false)}
          onSaved={() => { setEditHeaderOpen(false); reload(); }}
        />
      )}
    </div>
  );
}


/**
 * Inline "edit business & contact name" modal for the Partners admin
 * drawer (Feb 2026). Fixes the case where a PWS partner row was
 * created without a business_name and the admin couldn't see any
 * obvious way to fix it from this drawer.
 *
 * PWS franchisees use PATCH /admin/pws-partners/{id} (already accepts
 * partial updates and preserves lifecycle safety).
 * Non-PWS partners use PUT /admin/partners/{id} with the existing
 * partner row spread in — safe because we only mutate two string
 * fields, everything else is echoed back untouched.
 */
function PartnerEditHeaderModal({ partner, onClose, onSaved }) {
  const [businessName, setBusinessName] = useState(partner.business_name || "");
  const [contactName, setContactName]   = useState(partner.contact_name || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmedBiz = businessName.trim();
    if (!trimmedBiz) {
      toast.error("Business name is required");
      return;
    }
    setSaving(true);
    try {
      if (partner.partner_type === "pws_franchisee") {
        await api.patch(`/admin/pws-partners/${partner.id}`, {
          business_name: trimmedBiz,
          contact_name:  contactName.trim(),
        });
      } else {
        // PUT expects a full PartnerPayload — echo the existing row
        // and only override the two fields we're editing here.
        await api.put(`/admin/partners/${partner.id}`, {
          email:            partner.email,
          partner_type:     partner.partner_type,
          business_name:    trimmedBiz,
          contact_name:     contactName.trim(),
          phone:            partner.phone || "",
          website:          partner.website || "",
          referral_source:  partner.referral_source || "",
          notes:            partner.notes || "",
          status:           partner.status,
        });
      }
      toast.success("Partner details updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="admin-partner-edit-header-modal"
    >
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">Edit partner details</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Updates the top-level business & contact name shown on this drawer, the
              Partners list, and the PWS Partner Overview card.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Business name *</Label>
            <Input
              data-testid="admin-partner-edit-business-name"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Pristine Water Systems Cooloola"
              className="mt-1"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">Contact name</Label>
            <Input
              data-testid="admin-partner-edit-contact-name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="e.g. Sam Contact"
              className="mt-1"
            />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="admin-partner-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="admin-partner-edit-save"
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────
   A141 Phase 2 — Agency Users Section
   Admin surface to invite/deactivate/promote agency users. Lives in
   the PartnerDetail modal directly above the Lifecycle section for
   property_manager partners only.
   ───────────────────────────────────────────────────────────────────── */
function AgencyUsersSection({ partner }) {
  const [users, setUsers] = useState(null);
  const [primaryUserId, setPrimaryUserId] = useState(null);
  const [busy, setBusy] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("agency_staff");
  const [audit, setAudit] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const partnerId = partner.id;

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/partners/${partnerId}/agency-users`);
      setUsers(data.users || []);
      setPrimaryUserId(data.primary_user_id || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load agency users");
    }
  };

  const loadAudit = async () => {
    try {
      const { data } = await api.get(`/admin/partners/${partnerId}/agency-users/audit`);
      setAudit(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load audit");
    }
  };

  useEffect(() => { load(); }, [partnerId]);

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !inviteName.trim()) {
      toast.error("Email and full name are required");
      return;
    }
    setBusy("invite");
    try {
      const { data } = await api.post(
        `/admin/partners/${partnerId}/agency-users/invite`,
        { email: inviteEmail.trim(), full_name: inviteName.trim(), agency_role: inviteRole },
      );
      if (data.send_status === "sent") {
        toast.success(`Invite email sent to ${data.user.email}`);
      } else {
        toast.warning(`User created but email send ${data.send_status}: ${data.send_error || "unknown"}`);
      }
      setInviteEmail("");
      setInviteName("");
      setInviteRole("agency_staff");
      await load();
      if (showAudit) await loadAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not invite user");
    } finally {
      setBusy(null);
    }
  };

  const rowAction = async (label, url) => {
    setBusy(url);
    try {
      await api.post(url);
      toast.success(`User ${label}`);
      await load();
      if (showAudit) await loadAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${label}`);
    } finally {
      setBusy(null);
    }
  };

  const toggleAudit = async () => {
    const next = !showAudit;
    setShowAudit(next);
    if (next && audit === null) await loadAudit();
  };

  return (
    <div
      data-testid="agency-users-section"
      className="mt-6 rounded-2xl border border-cyan-200 bg-cyan-50/40 p-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-800 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Agency users
          </div>
          <div className="font-display text-base font-semibold text-slate-900 mt-0.5">
            {users === null ? "Loading…" : `${users.length} user${users.length === 1 ? "" : "s"} attached to this agency`}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Agency admins can manage other users (Phase 3, self-serve). Staff see the agency&apos;s properties/bookings/invoices/reports but cannot manage users.
          </div>
        </div>
        <button
          type="button"
          onClick={toggleAudit}
          data-testid="agency-users-audit-toggle"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
        >
          <History className="w-3.5 h-3.5" /> {showAudit ? "Hide" : "View"} audit trail
        </button>
      </div>

      <form
        onSubmit={submitInvite}
        data-testid="agency-users-invite-form"
        className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_150px_auto] gap-2"
      >
        <input
          type="email"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          data-testid="agency-users-invite-email"
          required
          placeholder="teammate@agency.example"
          className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm bg-white"
        />
        <input
          type="text"
          value={inviteName}
          onChange={(e) => setInviteName(e.target.value)}
          data-testid="agency-users-invite-name"
          required
          placeholder="Full name"
          className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm bg-white"
        />
        <select
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value)}
          data-testid="agency-users-invite-role"
          className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm bg-white"
        >
          <option value="agency_staff">Agency Staff</option>
          <option value="agency_admin">Agency Admin</option>
        </select>
        <button
          type="submit"
          disabled={busy !== null}
          data-testid="agency-users-invite-submit"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50"
        >
          <UserPlus className="w-3.5 h-3.5" /> Invite
        </button>
      </form>

      <div className="mt-4 space-y-2" data-testid="agency-users-list">
        {users === null && <div className="text-xs text-slate-500">Loading…</div>}
        {users && users.length === 0 && (
          <div className="text-xs text-slate-500">No users yet. Use the invite form above.</div>
        )}
        {users?.map((u) => {
          const isDisabled = u.status === "disabled" || u.disabled_at;
          const isAdmin = u.agency_role === "agency_admin";
          const isPrimary = u.id === primaryUserId;
          const rowBusy = busy?.includes(`/${u.id}/`);
          const url = (action) => `/admin/partners/${partnerId}/agency-users/${u.id}/${action}`;

          return (
            <div
              key={u.id}
              data-testid={`agency-user-row-${u.id}`}
              className={`rounded-xl border p-3 flex items-center justify-between gap-3 flex-wrap ${
                isDisabled ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-slate-900">{u.full_name || u.email}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    isAdmin ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-700"
                  }`} data-testid={`agency-user-role-${u.id}`}>
                    {isAdmin ? "Agency admin" : "Agency staff"}
                  </span>
                  {isPrimary && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800" data-testid={`agency-user-primary-${u.id}`}>
                      Primary
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    u.status === "invited" ? "bg-cyan-100 text-cyan-800" :
                    u.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                    u.status === "disabled" ? "bg-rose-100 text-rose-800" :
                    "bg-slate-100 text-slate-600"
                  }`} data-testid={`agency-user-status-${u.id}`}>
                    {u.status}
                  </span>
                </div>
                <div className="text-xs text-slate-600 mt-0.5 truncate">{u.email}</div>
                {u.invited_at && !isDisabled && (
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Invited {formatDateAU(u.invited_at)}
                    {u.last_invite_sent_at && u.last_invite_sent_at !== u.invited_at && (
                      <> · last resend {formatDateAU(u.last_invite_sent_at)}</>
                    )}
                  </div>
                )}
                {isDisabled && u.disabled_at && (
                  <div className="text-[11px] text-rose-700 mt-0.5">
                    Deactivated {formatDateAU(u.disabled_at)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {u.status === "invited" && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => rowAction("re-invited", url("resend-invite"))}
                    data-testid={`agency-user-resend-${u.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-slate-300 text-slate-700 hover:border-cyan-500 disabled:opacity-40"
                  >
                    <RefreshCcw className="w-3 h-3" /> Resend
                  </button>
                )}
                {!isAdmin && !isDisabled && (
                  <button
                    type="button"
                    disabled={rowBusy}
                    onClick={() => rowAction("promoted", url("promote"))}
                    data-testid={`agency-user-promote-${u.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-slate-300 text-slate-700 hover:border-violet-500 disabled:opacity-40"
                  >
                    <ArrowUpCircle className="w-3 h-3" /> Promote
                  </button>
                )}
                {isAdmin && !isDisabled && (
                  <button
                    type="button"
                    disabled={rowBusy}
                    onClick={() => rowAction("demoted", url("demote"))}
                    data-testid={`agency-user-demote-${u.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-slate-300 text-slate-700 hover:border-slate-500 disabled:opacity-40"
                  >
                    <ArrowDownCircle className="w-3 h-3" /> Demote
                  </button>
                )}
                {!isDisabled && (
                  <button
                    type="button"
                    disabled={rowBusy}
                    onClick={() => rowAction("deactivated", url("deactivate"))}
                    data-testid={`agency-user-deactivate-${u.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 border border-rose-200 text-rose-700 hover:border-rose-500 disabled:opacity-40"
                  >
                    <UserMinus className="w-3 h-3" /> Deactivate
                  </button>
                )}
                {isDisabled && (
                  <button
                    type="button"
                    disabled={rowBusy}
                    onClick={() => rowAction("reactivated", url("reactivate"))}
                    data-testid={`agency-user-reactivate-${u.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 border border-emerald-200 text-emerald-700 hover:border-emerald-500 disabled:opacity-40"
                  >
                    <UserCheck className="w-3 h-3" /> Reactivate
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showAudit && (
        <div className="mt-4 rounded-xl bg-white border border-slate-200 p-3" data-testid="agency-users-audit-list">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
            Audit trail ({audit?.length ?? "…"})
          </div>
          {audit === null && <div className="text-xs text-slate-500">Loading…</div>}
          {audit && audit.length === 0 && (
            <div className="text-xs text-slate-500">No agency user events recorded yet.</div>
          )}
          {audit && audit.length > 0 && (
            <ol className="space-y-2 max-h-64 overflow-y-auto">
              {audit.map((e) => (
                <li
                  key={e.id}
                  data-testid={`agency-user-audit-entry-${e.id}`}
                  className="text-xs border-l-2 border-cyan-300 pl-3"
                >
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-700">{e.action}</span>
                    <span className="text-slate-500">{formatDateTimeAU(e.at)}</span>
                  </div>
                  <div className="text-slate-700">
                    target user {(e.target_user_id || "").slice(0, 8)}
                    {e.previous_agency_role && e.new_agency_role && e.previous_agency_role !== e.new_agency_role && (
                      <> · {e.previous_agency_role} → <strong>{e.new_agency_role}</strong></>
                    )}
                  </div>
                  <div className="text-slate-500">
                    by {e.actor_email || e.actor_user_id || "—"}
                    {e.metadata?.recipient && <> · sent to {e.metadata.recipient}</>}
                    {e.metadata?.send_status && <> · {e.metadata.send_status}</>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────
   Partner Lifecycle Controls v1 (Feb 2026)
   Pause / Unpause · Archive / Restore · Resend invite · Audit trail
   · Delete (with 409 linked-record guard, "Archive instead" copy).
   Lives at the bottom of the PartnerDetail modal so the admin sees
   the partner's full context before running any lifecycle action.
   ───────────────────────────────────────────────────────────────────── */
function PartnerLifecycleSection({ partner, onChanged, onDeleted }) {
  const [busy, setBusy] = useState(null);
  const [reason, setReason] = useState("");
  const [audit, setAudit] = useState(null);   // null | array
  const [showAudit, setShowAudit] = useState(false);
  const [deleteBlock, setDeleteBlock] = useState(null); // { linked_counts, message }

  const status = (partner.status || "").toLowerCase();
  const isPaused = status === "paused";
  const isArchived = status === "archived";
  const pill = STATUS_PILL[status] || STATUS_PILL.lead;

  const call = async (label, url, body = {}) => {
    setBusy(label);
    try {
      await api.post(url, body);
      toast.success(`Partner ${label}`);
      setReason("");
      await onChanged?.();
      if (showAudit) await loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${label}`);
    } finally {
      setBusy(null);
    }
  };

  const loadAudit = async () => {
    try {
      const { data } = await api.get(`/admin/partners/${partner.id}/lifecycle-audit`);
      setAudit(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load audit trail");
    }
  };

  const toggleAudit = async () => {
    const next = !showAudit;
    setShowAudit(next);
    if (next && audit === null) await loadAudit();
  };

  const onHardDelete = async () => {
    if (!window.confirm(
      `Hard delete ${partner.business_name || partner.email}?\n\n` +
      `Only clean partners with ZERO bookings/properties/invoices/referrals/commissions can be deleted. ` +
      `Use Archive for anything with history.\n\nThis action cannot be undone.`
    )) return;
    setBusy("delete");
    try {
      await api.delete(`/admin/partners/${partner.id}`);
      toast.success("Partner deleted");
      onDeleted?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === "object" && detail.linked_counts) {
        setDeleteBlock(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not delete partner");
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="partner-lifecycle-section"
      className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/40 p-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-amber-800">Lifecycle controls</div>
          <div className="font-display text-base font-semibold text-slate-900 mt-0.5">
            Current status
            <span
              data-testid="partner-lifecycle-status-chip"
              className={`ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${pill.bg} ${pill.text}`}
            >
              {pill.label}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Paused and archived partners are blocked from portal login/access on the very next request.
          </div>
        </div>
        <button
          type="button"
          onClick={toggleAudit}
          data-testid="partner-lifecycle-audit-toggle"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
        >
          <History className="w-3.5 h-3.5" /> {showAudit ? "Hide" : "View"} audit trail
        </button>
      </div>

      {/* Reason input — shared across Pause / Archive / Restore / Unpause */}
      <label className="block mt-3">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
          Reason (optional — recorded in audit trail)
        </div>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="partner-lifecycle-reason"
          placeholder="e.g. Client offboarded · Complaint under review · Rebooked after 3 months"
          maxLength={1000}
          className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm bg-white"
        />
      </label>

      <div className="mt-3 flex flex-wrap gap-2" data-testid="partner-lifecycle-actions">
        {!isPaused && !isArchived && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => call("paused", `/admin/partners/${partner.id}/pause`, { reason })}
            data-testid="partner-lifecycle-pause-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
          >
            <Pause className="w-3.5 h-3.5" /> Pause access
          </button>
        )}
        {isPaused && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => call("unpaused", `/admin/partners/${partner.id}/unpause`, { reason })}
            data-testid="partner-lifecycle-unpause-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Play className="w-3.5 h-3.5" /> Unpause
          </button>
        )}
        {!isArchived && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => call("archived", `/admin/partners/${partner.id}/archive`, { reason })}
            data-testid="partner-lifecycle-archive-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            <Archive className="w-3.5 h-3.5" /> Archive
          </button>
        )}
        {isArchived && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => call("restored", `/admin/partners/${partner.id}/restore`, { reason })}
            data-testid="partner-lifecycle-restore-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50"
          >
            <ArchiveRestore className="w-3.5 h-3.5" /> Restore
          </button>
        )}
        <button
          type="button"
          disabled={busy !== null}
          onClick={onHardDelete}
          data-testid="partner-lifecycle-delete-btn"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>

      {/* Oct 2026 — Trade Portal invite now lives here for every
          portal-eligible partner type (property_manager, pws_franchisee,
          + the 7 generic trade types). Self-renders "not eligible" for
          everything else, so it's always safe to mount unconditionally.
          Supersedes the old /resend-invite button: this one also
          provisions a brand-new login on first send, not just resends. */}
      <TradePortalInvitePanel partnerId={partner.id} />

      {deleteBlock && (
        <div
          data-testid="partner-lifecycle-delete-blocked"
          className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3"
        >
          <div className="text-[11px] uppercase tracking-wider font-bold text-rose-800 mb-1 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" /> Delete blocked — linked records
          </div>
          <div className="text-xs text-rose-800">
            {deleteBlock.message || "This partner has linked records and cannot be hard-deleted."}
          </div>
          <ul className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-rose-900">
            {Object.entries(deleteBlock.linked_counts || {})
              .filter(([, v]) => (v || 0) > 0)
              .map(([label, n]) => (
                <li key={label} className="tabular-nums" data-testid={`partner-lifecycle-linked-${label}`}>
                  <span className="font-semibold">{n}</span>{" "}
                  <span className="text-rose-700">{label.replace(/_/g, " ")}</span>
                </li>
              ))}
          </ul>
          <div className="mt-2 text-[11px] text-rose-700">
            Recommendation: use <strong>Archive</strong> instead — the partner row is preserved for audit purposes but portal access is blocked.
          </div>
          <button
            type="button"
            onClick={() => setDeleteBlock(null)}
            className="mt-2 text-[11px] text-rose-700 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {showAudit && (
        <div className="mt-4 rounded-xl bg-white border border-slate-200 p-3" data-testid="partner-lifecycle-audit-list">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
            Audit trail ({audit?.length ?? "…"})
          </div>
          {audit === null && (
            <div className="text-xs text-slate-500">Loading…</div>
          )}
          {audit && audit.length === 0 && (
            <div className="text-xs text-slate-500">No lifecycle events recorded yet.</div>
          )}
          {audit && audit.length > 0 && (
            <ol className="space-y-2 max-h-64 overflow-y-auto">
              {audit.map((e) => (
                <li
                  key={e.id}
                  data-testid={`partner-lifecycle-audit-entry-${e.id}`}
                  className="text-xs border-l-2 border-amber-300 pl-3"
                >
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">{e.action}</span>
                    <span className="text-slate-500">{formatDateTimeAU(e.at)}</span>
                  </div>
                  <div className="text-slate-700">
                    {(e.previous_status || "—")} → <strong>{e.new_status || "—"}</strong>
                  </div>
                  <div className="text-slate-500">
                    by {e.actor_email || e.actor_user_id || "—"}
                    {e.metadata?.recipient && <> · sent to {e.metadata.recipient}</>}
                    {e.metadata?.send_status && <> · {e.metadata.send_status}</>}
                  </div>
                  {e.reason && (
                    <div className="mt-1 text-slate-600 italic">&ldquo;{e.reason}&rdquo;</div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}




function PartnerCreate({ onClose }) {
  const [form, setForm] = useState({ email: "", business_name: "", contact_name: "", phone: "", website: "", partner_type: "conveyancer", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/admin/partners", form);
      toast.success("Partner added");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSubmitting(false); }
  };
  return (
    <div data-testid="admin-partner-create-modal" className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-7">
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="font-display text-2xl font-bold text-slate-900">Add partner</h2>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div><Label className="text-xs">Email</Label><Input data-testid="partner-email" type="email" value={form.email} onChange={set("email")} className="mt-1" /></div>
          <div><Label className="text-xs">Business name</Label><Input data-testid="partner-business" value={form.business_name} onChange={set("business_name")} className="mt-1" /></div>
          <div><Label className="text-xs">Contact name</Label><Input data-testid="partner-contact" value={form.contact_name} onChange={set("contact_name")} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1" /></div>
            <div><Label className="text-xs">Website</Label><Input value={form.website} onChange={set("website")} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs">Type</Label>
            <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
              <SelectTrigger data-testid="partner-type" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="partner-create-submit" onClick={submit} disabled={submitting || !form.email} className="btn-aqua rounded-full font-semibold w-full">{submitting ? "Saving…" : "Add partner"}</Button>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────
   Phase 2L-A134 — Partner Pricing Controls v1
   Single-partner overrides section inside PartnerDetail modal.
   Lists:
     • partner-type defaults this partner currently inherits
     • single-partner overrides for THIS partner
   Actions: add override, disable override, view audit history.
   ───────────────────────────────────────────────────────────────────── */

const A134_PACKAGE_LABELS = {
  basic: "AWARE 4-Point",
  tank:  "AWARE Tank 360",
  bore:  "AWARE Bore 360",
};

const A134_ELIGIBLE_TYPES = [
  "property_manager", "conveyancer", "real_estate_agent", "buyer_agent",
  "pump_shop", "plumber", "building_inspector", "water_treatment_provider",
];

function PartnerPricingSection({ partner }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [draft, setDraft] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  const eligible = A134_ELIGIBLE_TYPES.includes(partner.partner_type);

  const load = () => {
    if (!eligible) {
      setState({ loading: false, error: null, data: { overrides: [], type_defaults: [], eligible_package_keys: ["basic", "tank", "bore"] } });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    api.get(`/admin/partners/${partner.id}/pricing`)
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((e) => setState({ loading: false, error: e.response?.data?.detail || "Could not load pricing", data: null }));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [partner.id]);

  const onCreate = async () => {
    if (!draft) return;
    try {
      await api.post(`/admin/partners/${partner.id}/pricing`, {
        package_key: draft.package_key,
        price_cents: Math.max(0, Math.round(Number(draft.price_dollars || 0) * 100)),
        notes: draft.notes || null,
      });
      toast.success(`Override added · ${A134_PACKAGE_LABELS[draft.package_key]}`);
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save override");
    }
  };

  const onDisable = async (row) => {
    if (!window.confirm(`Disable this override (${A134_PACKAGE_LABELS[row.package_key]})? This partner will fall back to the partner-type default for new bookings.`)) return;
    try {
      await api.delete(`/admin/pricing/${row.id}`);
      toast.success("Override disabled — new bookings fall back to partner-type default.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not disable override");
    }
  };

  const openAudit = async (row) => {
    setAuditFor({ id: row.id, label: A134_PACKAGE_LABELS[row.package_key] });
    try {
      const { data } = await api.get(`/admin/pricing/${row.id}/audit`);
      setAuditRows(data.items || []);
    } catch {
      toast.error("Could not load audit history");
    }
  };

  if (!eligible) {
    return (
      <div data-testid="partner-pricing-section" className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Pricing controls</div>
        <p className="mt-1 text-xs text-slate-500">
          Partner type <span className="font-semibold">{partner.partner_type || "—"}</span> is not eligible for partner_pricing in v1. PWS Franchisees keep their national pricing schedule.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="partner-pricing-section" className="mt-6 rounded-2xl border border-violet-200 bg-violet-50/40 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-violet-700">Pricing controls</div>
          <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Single-partner overrides</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Overrides beat the partner-type defaults below. Applies to NEW bookings only.
          </div>
        </div>
        <button
          onClick={() => setDraft({ package_key: "tank", price_dollars: 169, notes: "" })}
          data-testid="partner-pricing-add-override-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold"
        >
          <Plus className="w-3.5 h-3.5" /> Add override
        </button>
      </div>

      {state.loading && <div className="mt-3 text-xs text-slate-500">Loading pricing…</div>}
      {state.error && <div className="mt-3 text-xs text-rose-700">{state.error}</div>}

      {state.data && (
        <>
          {/* Single-partner overrides */}
          <div className="mt-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="partner-pricing-overrides-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Package</th>
                  <th className="text-right px-3 py-2">Override</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Notes</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {state.data.overrides.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-xs text-slate-500">
                      No single-partner overrides yet. This partner uses the partner-type defaults below.
                    </td>
                  </tr>
                )}
                {state.data.overrides.map((row) => (
                  <tr key={row.id} data-testid={`partner-override-row-${row.id}`} className={!row.active ? "opacity-50" : ""}>
                    <td className="px-3 py-2 text-slate-800">{A134_PACKAGE_LABELS[row.package_key] || row.package_key}</td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">${(row.price_cents / 100).toFixed(0)}</td>
                    <td className="px-3 py-2">
                      {row.active
                        ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Active</span>
                        : <span className="text-[10px] font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">Disabled</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs max-w-[200px] truncate">{row.notes || "—"}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => openAudit(row)}
                        data-testid={`partner-override-audit-${row.id}`}
                        className="text-xs text-slate-500 hover:text-slate-700 mr-3"
                      >
                        History
                      </button>
                      {row.active && (
                        <button
                          onClick={() => onDisable(row)}
                          data-testid={`partner-override-disable-${row.id}`}
                          className="text-xs text-rose-600 hover:text-rose-800"
                        >
                          Disable
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Inherited partner-type defaults */}
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Inherits partner-type defaults</div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm" data-testid="partner-type-defaults-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Package</th>
                    <th className="text-right px-3 py-2">Default</th>
                    <th className="text-left px-3 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {state.data.type_defaults.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-3 py-3 text-center text-xs text-slate-500">
                        No partner-type defaults set — bookings fall back to retail pricing.
                      </td>
                    </tr>
                  )}
                  {state.data.type_defaults.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2 text-slate-700">{A134_PACKAGE_LABELS[row.package_key] || row.package_key}</td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">${(row.price_cents / 100).toFixed(0)}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs max-w-[260px] truncate">{row.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {draft && (
        <div className="mt-4 rounded-xl border border-violet-200 bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-display font-semibold text-slate-900 text-sm">Add single-partner override</h4>
            <button onClick={() => setDraft(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Package</div>
              <select
                value={draft.package_key}
                onChange={(e) => setDraft({ ...draft, package_key: e.target.value })}
                data-testid="partner-override-package"
                className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm"
              >
                {(state.data?.eligible_package_keys || ["basic", "tank", "bore"]).map((p) => (
                  <option key={p} value={p}>{A134_PACKAGE_LABELS[p] || p}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Price (AUD)</div>
              <div className="inline-flex items-center gap-1 w-full">
                <span className="text-slate-500">$</span>
                <input
                  type="number" min={0} step={1}
                  value={draft.price_dollars}
                  onChange={(e) => setDraft({ ...draft, price_dollars: Number(e.target.value) })}
                  data-testid="partner-override-price"
                  className="flex-1 border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm"
                />
              </div>
            </label>
            <label className="block sm:col-span-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Notes</div>
              <input
                type="text"
                value={draft.notes || ""}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                data-testid="partner-override-notes"
                placeholder="Reason / context (e.g. agreed rate Feb-2026)"
                className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm"
              />
            </label>
          </div>
          <button
            onClick={onCreate}
            data-testid="partner-override-save"
            className="mt-3 px-4 py-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold"
          >
            Save override
          </button>
          <div className="mt-2 text-[10px] text-slate-500">Applies to NEW bookings only. Existing bookings keep the price they were created with.</div>
        </div>
      )}

      {auditFor && (
        <PartnerPricingAuditModal
          label={auditFor.label}
          rows={auditRows}
          onClose={() => { setAuditFor(null); setAuditRows([]); }}
        />
      )}
    </div>
  );
}

function PartnerPricingAuditModal({ label, rows, onClose }) {
  return (
    <div data-testid="partner-pricing-audit-modal" className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-display text-base font-semibold text-slate-900">Audit history</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-xs text-slate-500 mb-3">{label}</div>
        <ol className="space-y-3 max-h-80 overflow-y-auto">
          {rows.length === 0 && <li className="text-xs text-slate-500">No audit entries yet.</li>}
          {rows.map((e) => (
            <li key={e.id} className="text-xs leading-relaxed border-l-2 border-violet-300 pl-3">
              <div className="text-[10px] uppercase font-bold text-violet-700">{e.action}</div>
              <div className="text-slate-700 font-medium">{e.changed_by_email || e.changed_by_user_id}</div>
              <div className="text-slate-500">{formatDateTimeAU(e.changed_at)}</div>
              {e.action === "update" && (
                <div className="mt-1 text-slate-600">
                  {e.before?.price_cents !== e.after?.price_cents && (
                    <div>Price: ${(e.before?.price_cents || 0) / 100} → ${(e.after?.price_cents || 0) / 100}</div>
                  )}
                  {e.before?.notes !== e.after?.notes && <div>Notes updated</div>}
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Phase 2L-A135 — Partner Invoice Ledger v1 (READ-ONLY).
   Surfaces every PM/trade-partner invoice booking linked to this
   partner via `managed_by_partner_id`. No mutations — pricing,
   mark-paid, resend, and invoice generation are intentionally
   untouched.
   ───────────────────────────────────────────────────────────────────── */

const A135_INVOICE_ELIGIBLE_TYPES = [
  "property_manager", "conveyancer", "real_estate_agent", "buyer_agent",
  "pump_shop", "plumber", "building_inspector", "water_treatment_provider",
];

const A135_STATUS_PILL = {
  paid:      { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "Paid" },
  unpaid:    { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   label: "Unpaid" },
  overdue:   { bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200",    label: "Overdue" },
  cancelled: { bg: "bg-slate-100",  text: "text-slate-500",   border: "border-slate-200",   label: "Cancelled" },
};

function formatCentsAUD(cents) {
  const v = (cents || 0) / 100;
  return `$${v.toFixed(2)}`;
}

function PartnerInvoiceLedgerSection({ partner }) {
  const eligible = A135_INVOICE_ELIGIBLE_TYPES.includes(partner.partner_type);
  const [filter, setFilter] = useState("all");
  const [paidFrom, setPaidFrom] = useState("");
  const [paidTo, setPaidTo] = useState("");
  const [state, setState] = useState({ loading: true, error: null, data: null });
  // A135 v2 — per-row action state + mark-paid modal
  const [busyRef, setBusyRef] = useState(null);
  const [markPaidRef, setMarkPaidRef] = useState(null);   // reference for open modal
  const [markPaidForm, setMarkPaidForm] = useState({
    amount_paid: "",
    payment_method: "bank_transfer",
    payment_reference: "",
    notes: "",
    paid_at: "",
  });

  const load = (f = filter, pf = paidFrom, pt = paidTo) => {
    if (!eligible) {
      setState({ loading: false, error: null, data: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    const params = { status: f };
    if (pf) params.paid_from = pf;
    if (pt) params.paid_to   = pt;
    api.get(`/admin/partners/${partner.id}/invoices`, { params })
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((e) => setState({ loading: false, error: e.response?.data?.detail || "Could not load ledger", data: null }));
  };

  useEffect(() => { load(filter, paidFrom, paidTo); /* eslint-disable-next-line */ }, [partner.id, filter, paidFrom, paidTo]);

  // A135 v2 — per-row admin actions. Both endpoints already exist and
  // are require_admin (payments.py). We just wire the UI + reload.
  const onResend = async (reference) => {
    setBusyRef(`resend:${reference}`);
    try {
      const { data } = await api.post(`/admin/bookings/${reference}/resend-invoice-email`);
      toast.success(`Invoice email re-sent to ${data.recipient || "the agency"}`);
      load(filter, paidFrom, paidTo);
    } catch (e) {
      toast.error(formatApiError(e, "Could not resend invoice email"));
    } finally {
      setBusyRef(null);
    }
  };

  const openMarkPaid = (row) => {
    setMarkPaidRef(row.reference);
    setMarkPaidForm({
      amount_paid: (row.amount || 0).toFixed(2),
      payment_method: "bank_transfer",
      payment_reference: "",
      notes: "",
      paid_at: new Date().toISOString().slice(0, 10),
    });
  };

  const submitMarkPaid = async () => {
    const ref = markPaidRef;
    setBusyRef(`markpaid:${ref}`);
    try {
      const body = {
        amount_paid: parseFloat(markPaidForm.amount_paid),
        payment_method: markPaidForm.payment_method,
      };
      if (markPaidForm.payment_reference.trim()) body.payment_reference = markPaidForm.payment_reference.trim();
      if (markPaidForm.notes.trim())             body.notes             = markPaidForm.notes.trim();
      // A135 v2 fix — never send a future timestamp. If the admin picks
      // today's date we snap to the current instant (safe because the
      // backend validator only rejects paid_at > now). For any past date
      // we snap to 00:00Z of that day so we can't drift into the future
      // due to the caller's local timezone.
      if (markPaidForm.paid_at) {
        const today = new Date().toISOString().slice(0, 10);
        body.paid_at = markPaidForm.paid_at === today
          ? new Date().toISOString()
          : new Date(`${markPaidForm.paid_at}T00:00:00Z`).toISOString();
      }
      await api.post(`/admin/bookings/${ref}/mark-paid-manual`, body);
      toast.success(`${ref} marked paid`);
      setMarkPaidRef(null);
      load(filter, paidFrom, paidTo);
    } catch (e) {
      toast.error(formatApiError(e, "Could not mark invoice paid"));
    } finally {
      setBusyRef(null);
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams({ status: filter });
    if (paidFrom) params.set("paid_from", paidFrom);
    if (paidTo)   params.set("paid_to",   paidTo);
    const url = `${API_BASE_FOR_LEDGER}/admin/partners/${partner.id}/invoices.csv?${params.toString()}`;
    window.open(url, "_blank");
  };

  if (!eligible) {
    return (
      <div data-testid="partner-invoice-ledger-section" className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Invoice ledger</div>
        <p className="mt-1 text-xs text-slate-500">
          Partner type <span className="font-semibold">{partner.partner_type || "—"}</span> does not raise account invoices, so there is no ledger to show.
        </p>
      </div>
    );
  }

  const totals = state.data?.totals;

  return (
    <div data-testid="partner-invoice-ledger-section" className="mt-6 rounded-2xl border border-cyan-200 bg-cyan-50/40 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-700">Invoice ledger</div>
          <div className="font-display text-base font-semibold text-slate-900 mt-0.5">Account invoices · this partner</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Read-only view. Read from <code className="text-[10px] bg-white px-1 rounded">managed_by_partner_id</code> · <code className="text-[10px] bg-white px-1 rounded">pm_booking=true</code>.
          </div>
        </div>
      </div>

      {/* Summary tiles */}
      {totals && (
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="partner-invoice-ledger-totals">
          <LedgerTile
            testid="ledger-tile-outstanding"
            label="Outstanding"
            value={formatCentsAUD(totals.outstanding_cents)}
            hint={`${totals.outstanding_count} invoice${totals.outstanding_count === 1 ? "" : "s"}`}
            accent="amber"
            icon={Receipt}
          />
          <LedgerTile
            testid="ledger-tile-overdue"
            label="Overdue"
            value={formatCentsAUD(totals.overdue_cents)}
            hint={`${totals.overdue_count} invoice${totals.overdue_count === 1 ? "" : "s"}`}
            accent="rose"
            icon={AlertCircle}
          />
          <LedgerTile
            testid="ledger-tile-paid"
            label="Paid"
            value={formatCentsAUD(totals.paid_cents)}
            hint={`${totals.paid_count} invoice${totals.paid_count === 1 ? "" : "s"}`}
            accent="emerald"
            icon={DollarSign}
          />
          <LedgerTile
            testid="ledger-tile-count"
            label="Total invoices"
            value={String(totals.invoice_count)}
            hint={totals.cancelled_count > 0 ? `${totals.cancelled_count} cancelled` : "All-time"}
            accent="cyan"
            icon={FileText}
          />
        </div>
      )}

      {/* Filter tabs */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5" role="tablist" data-testid="partner-invoice-ledger-filters">
        {[
          { k: "all",         label: "All" },
          { k: "outstanding", label: "Outstanding" },
          { k: "overdue",     label: "Overdue" },
          { k: "paid",        label: "Paid" },
        ].map((t) => {
          const active = filter === t.k;
          return (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              data-testid={`partner-invoice-filter-${t.k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                active
                  ? "bg-cyan-600 border-cyan-600 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-cyan-300"
              }`}
              role="tab"
              aria-selected={active}
            >
              {t.label}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={exportCsv}
            disabled={!state.data || state.loading}
            data-testid="partner-invoice-ledger-export-csv"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-slate-300 text-slate-700 hover:border-cyan-500 hover:text-cyan-700 disabled:opacity-40"
            title="Export the current filtered view as CSV"
          >
            <Download className="w-3 h-3" /> Export CSV
          </button>
        </div>
      </div>

      {/* Paid-date filter — visible when the paid filter is active or
          when either bound is already set (so admins can see & clear it). */}
      {(filter === "paid" || paidFrom || paidTo) && (
        <div
          data-testid="partner-invoice-paid-date-filter"
          className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600"
        >
          <span className="font-semibold text-slate-700">Paid date range:</span>
          <label className="inline-flex items-center gap-1">
            <span className="text-[11px]">from</span>
            <input
              type="date"
              value={paidFrom}
              onChange={(e) => setPaidFrom(e.target.value)}
              data-testid="partner-invoice-paid-from"
              className="border border-slate-300 rounded px-2 py-1 text-xs bg-white"
            />
          </label>
          <label className="inline-flex items-center gap-1">
            <span className="text-[11px]">to</span>
            <input
              type="date"
              value={paidTo}
              onChange={(e) => setPaidTo(e.target.value)}
              data-testid="partner-invoice-paid-to"
              className="border border-slate-300 rounded px-2 py-1 text-xs bg-white"
            />
          </label>
          {(paidFrom || paidTo) && (
            <button
              type="button"
              onClick={() => { setPaidFrom(""); setPaidTo(""); }}
              data-testid="partner-invoice-paid-date-clear"
              className="text-cyan-700 hover:text-cyan-900 font-semibold text-[11px] underline"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {state.loading && <div className="mt-3 text-xs text-slate-500">Loading ledger…</div>}
      {state.error && <div className="mt-3 text-xs text-rose-700" data-testid="partner-invoice-ledger-error">{state.error}</div>}

      {state.data && (
        <div className="mt-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Filter feedback strip — makes it obvious which filter is
              active even when zero rows match (fixes the "did it click?"
              UX issue spotted in production verification). */}
          <div
            data-testid="partner-invoice-ledger-result-summary"
            className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] text-slate-600 flex items-center justify-between gap-2"
          >
            <span>
              Showing{" "}
              <span className="font-semibold text-slate-900" data-testid="ledger-shown-count">
                {state.data.items.length}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-slate-900">{state.data.totals?.invoice_count ?? 0}</span>{" "}
              {filter === "all" ? "invoices" : (
                <>
                  ·{" "}
                  <span className="font-semibold text-cyan-700 capitalize">{filter}</span>{" "}
                  only
                </>
              )}
            </span>
            {filter !== "all" && (
              <button
                onClick={() => setFilter("all")}
                data-testid="partner-invoice-filter-reset"
                className="text-cyan-700 hover:text-cyan-900 font-semibold"
              >
                Show all →
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="partner-invoice-ledger-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Reference</th>
                  <th className="text-left px-3 py-2">Property</th>
                  <th className="text-left px-3 py-2">Test</th>
                  <th className="text-left px-3 py-2">PO #</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Due</th>
                  <th className="text-left px-3 py-2">Paid</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {state.data.items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-xs text-slate-500" data-testid="partner-invoice-ledger-empty">
                      {filter === "overdue"     ? "No overdue invoices for this partner."
                       : filter === "outstanding" ? "No outstanding invoices for this partner — everything is up to date."
                       : filter === "paid"        ? "No paid invoices yet for this partner."
                       : "No invoices linked to this partner yet."}
                    </td>
                  </tr>
                )}
                {state.data.items.map((row) => {
                  const pill = A135_STATUS_PILL[row.status] || A135_STATUS_PILL.unpaid;
                  return (
                    <tr key={row.reference} data-testid={`partner-invoice-row-${row.reference}`}>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-800">{row.reference}</td>
                      <td className="px-3 py-2.5 text-slate-700 text-xs max-w-[200px] truncate" title={row.property_address || ""}>
                        {row.property_address || "—"}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">{row.test_name || row.test_id || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">{row.purchase_order_number || "—"}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-slate-900 tabular-nums">${(row.amount || 0).toFixed(2)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${pill.bg} ${pill.text} ${pill.border}`}>
                          {pill.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-slate-500 text-[11px]">{row.payment_due_at ? formatDateAU(row.payment_due_at) : "—"}</td>
                      <td className="px-3 py-2.5 text-slate-500 text-[11px]">{row.paid_at ? formatDateAU(row.paid_at) : "—"}</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          {/* A135 v2 — Resend / Mark-paid only when row is
                              actionable (unpaid / overdue). Paid + cancelled
                              rows are frozen. */}
                          {(row.status === "unpaid" || row.status === "overdue") && (
                            <>
                              <button
                                type="button"
                                onClick={() => onResend(row.reference)}
                                disabled={busyRef === `resend:${row.reference}`}
                                data-testid={`partner-invoice-resend-${row.reference}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border border-cyan-300 bg-white text-cyan-700 hover:bg-cyan-50 disabled:opacity-40"
                                title="Re-send the invoice email to the agency"
                              >
                                {busyRef === `resend:${row.reference}`
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <Send className="w-3 h-3" />}
                                Resend
                              </button>
                              <button
                                type="button"
                                onClick={() => openMarkPaid(row)}
                                disabled={busyRef === `markpaid:${row.reference}`}
                                data-testid={`partner-invoice-mark-paid-${row.reference}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                                title="Record an off-Stripe payment against this invoice"
                              >
                                <CheckCircle2 className="w-3 h-3" /> Mark paid
                              </button>
                            </>
                          )}
                          <a
                            href={`${API_BASE_FOR_LEDGER}/bookings/${row.reference}/invoice.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`partner-invoice-download-${row.reference}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900"
                          >
                            <Download className="w-3 h-3" /> PDF
                          </a>
                          <Link
                            to={`/admin/bookings/${row.reference}`}
                            data-testid={`partner-invoice-open-${row.reference}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"
                          >
                            <ExternalLink className="w-3 h-3" /> Open
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* A135 v2 — Mark-Paid modal. Rendered inside the ledger section so
          it inherits the scroll container; z-[70] sits above the partner
          detail modal (z-50) that contains this ledger. */}
      {markPaidRef && (
        <MarkPaidInvoiceModal
          reference={markPaidRef}
          form={markPaidForm}
          setForm={setMarkPaidForm}
          submitting={busyRef === `markpaid:${markPaidRef}`}
          onCancel={() => setMarkPaidRef(null)}
          onSubmit={submitMarkPaid}
        />
      )}
    </div>
  );
}

/* Mark-Paid modal — split out for readability. Handled here (not shared)
   because the form shape is A135-specific and reuses the ledger's local
   state. */
function MarkPaidInvoiceModal({ reference, form, setForm, submitting, onCancel, onSubmit }) {
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const disabled =
    submitting ||
    !form.amount_paid ||
    Number(form.amount_paid) <= 0 ||
    !form.payment_method;

  return (
    <div
      data-testid="mark-paid-modal"
      className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={submitting ? undefined : onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              Mark invoice paid
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Record an off-Stripe payment against{" "}
              <span className="font-mono font-semibold text-slate-700">
                {reference}
              </span>
              . This action is idempotent — a paid invoice cannot be marked paid twice.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-40"
            data-testid="mark-paid-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Amount paid (AUD)
            </span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={form.amount_paid}
              onChange={(e) => update({ amount_paid: e.target.value })}
              data-testid="mark-paid-amount"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            />
          </label>

          <label className="block">
            <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Payment method
            </span>
            <select
              value={form.payment_method}
              onChange={(e) => update({ payment_method: e.target.value })}
              data-testid="mark-paid-method"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
              disabled={submitting}
            >
              <option value="bank_transfer">Bank transfer</option>
              <option value="eft">EFT</option>
              <option value="cheque">Cheque</option>
              <option value="other">Other</option>
            </select>
          </label>

          <label className="block">
            <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Payment date
            </span>
            <input
              type="date"
              value={form.paid_at}
              onChange={(e) => update({ paid_at: e.target.value })}
              data-testid="mark-paid-date"
              max={new Date().toISOString().slice(0, 10)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            />
            <span className="text-[10px] text-slate-500 mt-0.5 block">
              Defaults to today. Cannot be in the future.
            </span>
          </label>

          <label className="block">
            <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Reference (optional)
            </span>
            <input
              type="text"
              value={form.payment_reference}
              onChange={(e) => update({ payment_reference: e.target.value })}
              data-testid="mark-paid-reference"
              placeholder="e.g. bank txn id, cheque #"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
              maxLength={200}
            />
          </label>

          <label className="block">
            <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Notes (optional)
            </span>
            <textarea
              value={form.notes}
              onChange={(e) => update({ notes: e.target.value })}
              data-testid="mark-paid-notes"
              rows={2}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none"
              disabled={submitting}
              maxLength={2000}
            />
          </label>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            data-testid="mark-paid-cancel-btn"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={disabled}
            data-testid="mark-paid-submit-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            {submitting
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <CheckCircle2 className="w-3.5 h-3.5" />}
            {submitting ? "Marking paid…" : "Confirm — mark paid"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LedgerTile({ testid, label, value, hint, accent, icon: Icon }) {
  const styles = {
    amber:   "text-amber-700 bg-amber-50 border-amber-200",
    rose:    "text-rose-700 bg-rose-50 border-rose-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    cyan:    "text-cyan-700 bg-cyan-50 border-cyan-200",
  }[accent] || "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-xl border bg-white p-3`}>
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-1.5 ${styles}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">{label}</div>
      <div className="font-display text-xl font-bold text-slate-900 mt-0.5 tabular-nums">{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

// Backend base URL for direct invoice PDF link (the api axios client adds
// /api prefix automatically; here we need the raw path for a browser <a>).
const API_BASE_FOR_LEDGER = `${process.env.REACT_APP_BACKEND_URL}/api`;

